<template>
  <div class="smart-actions__wrapper">
    <div class="navigation">
      <template v-if="!isProcessing">
        <span class="navigation__lebel">Smart Actions: </span>
        <template v-if="isTranscriptionEmpty">
          <ButtonComponent
            variant="ai-purple"
            icon="general.stars"
            suffix-icon="general.menuArrow"
            data-testid="ai-summary-trigger"
            disabled
            class="menu-trigger"
            >AI Summary</ButtonComponent
          >
        </template>
        <template v-else>
          <ContextMenuWrapper placement="bottom-start" class="smart-actions__menu">
            <ZtHighlightBox variant="wiggle">
              <ButtonComponent
                variant="ai-purple"
                :icon="isLocked ? 'general.lockKey' : 'general.stars'"
                suffix-icon="general.menuArrow"
                data-testid="ai-summary-trigger"
                :submit="true"
                class="menu-trigger"
                >AI Summary</ButtonComponent
              >
            </ZtHighlightBox>
            <template #menu>
              <li v-if="isLocked" class="info-node" data-testid="smart-actions-locked-info">
                <div class="info-node__header">
                  <p class="info-node__title">Unlock access to Zight AI</p>
                  <p v-if="isQuotaExhausted && nextResetDate" class="info-node__description">
                    Resets {{ nextResetDate }}. Upgrade for unlimited Smart Actions.
                  </p>
                  <p class="info-node__description">Record, Relax, and Let AI Do the Rest.</p>
                </div>
                <p class="info-node__description">Transform videos into instant insights. Automate SOPs, meeting notes, training materials, and more.</p>
                <ButtonComponent variant="ai" icon-size="sm" icon="general.stars" @click.prevent="explorePlans">Explore our plans</ButtonComponent>
              </li>
              <li class="menu-node menu-node-border">
                <div class="buttons-group">
                  <ButtonComponent
                    variant="ai"
                    icon-size="xs"
                    icon="ai.generalSummary"
                    @click.prevent="aiMetadataStore.useDefaultSummary()"
                    data-testid="summary-default"
                  >
                    General summary (default)
                  </ButtonComponent>
                </div>
              </li>
              <li class="menu-node" :class="{ 'actions-locked': isLocked }" v-for="category in getSortedCategories" :key="category.key">
                <p class="group-title">
                  {{ category.key }}
                  <span v-if="isSuggestedCategory(category.key)" class="suggested-badge">Suggested</span>
                </p>
                <div class="buttons-group">
                  <ButtonComponent
                    v-for="smartAction in category.subcategories"
                    :key="smartAction.key"
                    variant="ai"
                    icon-size="xs"
                    :icon="getIconForKey(smartAction.key)"
                    :data-testid="`smart-action-${smartAction.key}`"
                    @click.prevent="generateSmartAction(smartAction.key)"
                    :disabled="isLocked"
                    >{{ smartAction.title }}</ButtonComponent
                  >
                </div>
              </li>
            </template>
          </ContextMenuWrapper>
        </template>
        <div v-show="suggestedSubcategories.length" class="hidden md:flex items-center gap-2 ai-button-compact" data-testid="inline-suggested-smart-actions">
          <span class="navigation__lebel">Suggested: </span>
          <div class="flex gap-2">
            <ButtonComponent
              v-for="action in suggestedSubcategories"
              :key="action.key"
              variant="ai"
              icon-size="xs"
              :icon="getIconForKey(action.key)"
              :disabled="isLocked"
              @click.prevent="generateSmartAction(action.key)"
              >{{ action.title }}</ButtonComponent
            >
          </div>
        </div>
        <div v-show="isTranscriptionEmpty" class="flex gap-1" data-testid="no-voice-detected">
          <Icon name="general.info" size="sm" />
          <span class="flex gap-2">No voice detected</span>
        </div>
      </template>
      <div v-else class="ai-processing" data-testid="ai-processing">
        <ButtonComponent variant="ai" icon="general.stars" suffix-icon="general.menuArrow" data-testid="ai-summary-trigger" disabled class="menu-trigger"
          >AI Summary</ButtonComponent
        >
        <div class="ai-processing__icon">
          <Icon name="ai.processing" size="sm" />
        </div>
        {{ displayProcessingMessage }}
      </div>
    </div>
    <div class="flex items-center gap-4">
      <ButtonComponent
        v-if="description"
        variant="link"
        icon-size="base"
        icon="general.generalExport"
        data-testid="viewer-export-summary"
        @click.prevent="openShareModalAction({ modalProps: { selectedTab: '#export' } })"
        >Export</ButtonComponent
      >
      <SmartActionsSettings />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon, ButtonComponent } from '@/storybook';
import { ContextMenuWrapper } from '@/storybook/menu';
import { ZtHighlightBox } from '@/storybook/highlightbox';
import SmartActionsSettings from './SmartActionsSettings.vue';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useEntitlementsStore } from '@/store/useEntitlementsStore';
import { QuotaFeature } from '@/types/entitlements';
import { useAiProcessingMessage } from '@/modules/viewer/composables/useAiProcessingMessage';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';

const itemStore = useViewerItemStore();
const aiMetadataStore = useAiMetadataStore();
const smartActionsStore = useSmartActionsStore();
const transcriptionStore = useTranscriptionStore();
const entitlementsStore = useEntitlementsStore();

const { isProcessing: isAiMetadataProcessing } = storeToRefs(aiMetadataStore);
const { getSortedCategories, isActioning, primaryCategoryKey } = storeToRefs(smartActionsStore);
const { isEditProcessing } = storeToRefs(itemStore);

const isQuotaExhausted = computed(() => entitlementsStore.isFeatureQuotaExhausted(QuotaFeature.SMART_ACTIONS));
const isLocked = computed(() => entitlementsStore.isFeatureLocked(QuotaFeature.SMART_ACTIONS));
const nextResetDate = computed(() => entitlementsStore.quotaResetDate(QuotaFeature.SMART_ACTIONS));
const { isTranscriptionProcessing, isTranscriptionEmpty } = storeToRefs(transcriptionStore);
const { description } = storeToRefs(itemStore);
const { openShareModalAction } = useItemActions();

const explorePlans = () => {
  window.location.href = '/account/plans';
};

const icons: { [key: string]: string } = {
  meeting_summary: 'ai.meetingSummary',
  action_items: 'ai.actionItems',

  step_by_step_guide: 'ai.stepByStepGuide',
  technical_documentation: 'ai.technicalDocumentation',
  blog_post: 'ai.blogPost',

  bug_report: 'ai.bugReport',
  feature_request: 'ai.keyTakeaways',

  sales_pitch: 'ai.salesPitch',
  email: 'ai.email',
};

const displayProcessingMessage = useAiProcessingMessage();

const isProcessing = computed(() => isAiMetadataProcessing.value || isActioning.value || isEditProcessing.value || isTranscriptionProcessing.value);

const getIconForKey = (key: keyof typeof icons) => {
  return icons[key] || 'general.defaultIcon'; // Fallback icon
};

const isSuggestedCategory = (category: string) => {
  return (primaryCategoryKey?.value ?? '') === category;
};

const generateSmartAction = (key: string) => {
  if (isLocked.value) {
    return;
  }
  smartActionsStore.requestSmartAction(key);
};

const suggestedSubcategories = computed(() => {
  const key = primaryCategoryKey?.value;
  if (!key) return [];
  const cat = getSortedCategories.value.find(c => c.key === key);
  return cat ? cat.subcategories : [];
});
</script>

<style scoped>
.smart-actions__wrapper {
  @apply flex justify-between m-4 pb-2 border-b border-slate-300 dark:border-zinc-700;
}
.smart-actions__menu :deep(.context-menu) {
  @apply rounded-10;
}
.smart-actions__menu li + li {
  @apply mt-4;
}
.smart-actions__menu .info-node {
  @apply flex flex-col items-start gap-2 p-4 border border-slate-300 dark:border-zinc-700 rounded-10;
}
.smart-actions__menu .info-node__header {
  @apply flex flex-col m-0;
}
.smart-actions__menu .info-node__title {
  @apply text-sm font-bold dark:text-zinc-200 m-0;
}
.smart-actions__menu .info-node__description {
  @apply text-sm text-zinc-500 dark:text-zinc-400 m-0;
  max-width: 20rem;
}
.smart-actions__menu .info-node :deep(.button) {
  --button-block-size: 2rem;
}
.actions-locked {
  @apply opacity-40 pointer-events-none;
}
.smart-actions__menu .menu-node-border {
  @apply pb-4 border-b border-slate-300 dark:border-zinc-700;
}
.smart-actions__menu li :deep(.button),
.ai-button-compact :deep(.button) {
  --button-block-size: 1.75rem;
  --button-gap: 0.25rem;
}

.menu-node {
  @apply flex flex-wrap flex-col gap-2;
}

.buttons-group {
  @apply flex gap-2;
}

.navigation {
  @apply flex flex-wrap gap-4 items-center;
}

.navigation__lebel {
  @apply text-sm dark:text-zinc-300 font-semibold;
}

.group-title {
  @apply capitalize text-sm dark:text-zinc-300 font-semibold;
}
.ai-processing {
  @apply flex gap-2 text-sm dark:text-zinc-300 font-semibold items-center min-h-[2.25rem];
}
.ai-processing__icon {
  @apply w-7 h-7 p-1 flex items-center justify-start;
  border-radius: 0.625rem;
  background: linear-gradient(0deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%), linear-gradient(90deg, #f5d9b7 0%, #e0adff 50%, #f5d9b7 100%);
  background-size: 200% 200%;
  animation: animateGlow 1.25s linear infinite;
}
.suggested-badge {
  @apply text-xs dark:text-zinc-300 font-semibold bg-orange-200 dark:bg-zinc-600 rounded-4 ml-2 px-2 py-1;
}
@keyframes animateGlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}
</style>
